import { makeid } from 'utils/helpers/makeid';

import {
  additionalFollowUpItem,
  freeTextCommentsItem,
  isPatientCROorDNCItem,
  needEscalateItem,
  twoWaySyncInfoItem,
  uniqueDaysItem,
} from '../common';

import type { TFormItem } from '../../types';

export const glucose: TFormItem[] = [
  {
    key: makeid(10),
    title: <p>Is the glucometer functioning properly?</p>,
    type: 'radio',
    required: false,
    name: 'glucoseDeviceFunctioningProperly',
    options: [
      {
        label: 'Yes',
        value: true,
        key: makeid(10),
      },
      {
        label: 'No (describe below; provide patient assistance if possible)',
        value: false,
        key: makeid(10),
      },
    ],
    items: [
      {
        type: 'textarea',
        name: 'glucoseDeviceFunctioningProperlyComment',
        title: null,
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Is the patient awaiting any device replacements, troubleshooting or
        supplies?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'waitingDeviceReplacement',
    options: [
      {
        label: 'Yes',
        value: true,
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            type: 'checkbox',
            name: 'waitingForDeviceReplacementList',
            title: null,
            key: makeid(10),
            options: [
              {
                label: 'device replacement',
                value: 'device replacement',
                key: makeid(10),
              },
              {
                label: 'troubleshooting',
                value: 'troubleshooting',
                key: makeid(10),
              },
              { label: 'supplies', value: 'supplies', key: makeid(10) },
            ],
          },
        ],
      },
      {
        label:
          'No (describe in freetext; provide patient assistance if possible)',
        value: false,
        key: makeid(10),
        subItems: [
          {
            type: 'textarea',
            name: 'waitingForDeviceReplacementComment',
            title: null,
            key: makeid(10),
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: null,
    type: 'empty',
    name: '_empty',
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Number of at-risk glucose readings reviewed: <span>__</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'atRiskNumber',
        validation: {
          min: 0,
        },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Highest glucose value: <span>__ mg/dL</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestGlucose',
        validation: {
          min: 0,
        },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest glucose value: <span>__ mg/dL</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestGlucose',
        validation: {
          min: 0,
        },
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>What type of at-risk trend(s) were observed?</p>,
    type: 'radio',
    name: 'typeofTrend',
    options: [
      {
        label: 'High readings',
        value: 'high',
        key: makeid(10),
      },
      {
        label: 'Low readings',
        value: 'low',
        key: makeid(10),
      },
      {
        label: 'Both',
        value: 'both',
        key: makeid(10),
      },
      {
        label: 'Other trend (describe in free text)',
        value: 'other',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            type: 'input',
            name: 'otherTypeofTrend',
            title: null,
            key: makeid(10),
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Compared to the last review, have the total number of at-risk readings:
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'comparedToLastReview',
    options: [
      { label: 'Increased', value: 'increased', key: makeid(10) },
      { label: 'Decreased', value: 'decreased', key: makeid(10) },
      {
        label: 'Remained about the same',
        value: 'remained about the same',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: (
      <p>
        Has the number or trend of at-risk readings changed compared to the last
        review?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'comparedToLastReviewTrend',
    options: [
      { label: 'Trend improving', value: 'improving', key: makeid(10) },
      { label: 'Trend worsening', value: 'worsening', key: makeid(10) },
      {
        label: 'No significant change',
        value: 'about the same',
        key: makeid(10),
      },
    ],
  },
  { ...uniqueDaysItem('glucose') },
  {
    key: makeid(10),
    title: (
      <p>
        Review existing clinical documentation: Did the patient provide
        explanations for any at-risk values (diet, illness, missed medication,
        device issue)?
      </p>
    ),
    type: 'radio',
    required: false,
    name: 'reviewClinicalDocumentation',
    options: [
      { label: 'Yes (describe in free text)', value: true, key: makeid(10) },
      { label: 'No', value: false, key: makeid(10) },
    ],
    items: [
      {
        key: makeid(10),
        type: 'textarea',
        name: 'reviewClinicalDocumentationComment',
        title: null,
      },
    ],
  },
  { ...twoWaySyncInfoItem },
  { ...isPatientCROorDNCItem },
  { ...additionalFollowUpItem },
  { ...needEscalateItem },
  { ...freeTextCommentsItem },
];
